/**
 * CalendarInviteModal Component
 *
 * Modal for generating Outlook calendar invites for confirmed ticket requests.
 * Opens directly in Outlook Web App with the invite pre-filled.
 *
 * Features:
 * - Select from available events
 * - Enter guest details (name, email, tickets confirmed)
 * - Confirm which option was confirmed (Primary, 2nd, or 3rd choice)
 * - Include default email signature from saved settings
 * - Open directly in Outlook Web App (no file download needed)
 *
 * Based on: F:/1 - Work - Daily Automated Apps/Outlook_Calendar_Availability_Reporter
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  X,
  Calendar,
  ExternalLink,
  User,
  Mail,
  Ticket,
  CheckCircle,
  ChevronDown,
  Search,
  Loader2,
} from 'lucide-react';
import type { HMCEvent } from '../hmcEventsStore';
import {
  openOutlookCalendarInvite,
  type CalendarInviteConfig,
} from '../utils/calendarInviteGenerator';
import { getSetting } from '@shared/services/userSettingsService';

// Namespace for HMC email defaults (shared with EmailTemplateModal)
const SETTINGS_NAMESPACE = 'hmc-email';

interface CalendarInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  events: HMCEvent[];
}

type OptionConfirmed = 1 | 2 | 3;

/**
 * Clean event name (remove attendance info)
 */
function getCleanEventName(opponent: string | null): string {
  if (!opponent) return 'Event';
  const cleaned = opponent
    .replace(/\s*[-–]\s*\d+\s*(tickets?|tix|seats?).*$/i, '')
    .replace(/\s*\(\d+\s*(tickets?|tix|seats?)\).*$/i, '')
    .replace(/\s*\([^)]+\)\s*$/, '')
    .trim();
  return cleaned || 'Event';
}

/**
 * Format event date for display
 */
function formatEventDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export const CalendarInviteModal: React.FC<CalendarInviteModalProps> = ({
  isOpen,
  onClose,
  events,
}) => {
  // Form state
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [axsEmail, setAxsEmail] = useState('');
  const [ticketsConfirmed, setTicketsConfirmed] = useState(2);
  const [optionConfirmed, setOptionConfirmed] = useState<OptionConfirmed>(1);

  // Email signature from saved settings
  const [signatureHtml, setSignatureHtml] = useState('');
  const [loadingSignature, setLoadingSignature] = useState(false);

  // Event dropdown state
  const [showEventDropdown, setShowEventDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Success state
  const [showSuccess, setShowSuccess] = useState(false);

  // Load email signature from saved settings on modal open
  useEffect(() => {
    if (isOpen) {
      const loadSignature = async () => {
        setLoadingSignature(true);
        try {
          const savedSignature = await getSetting<string>('email-signature', {
            namespace: SETTINGS_NAMESPACE,
            defaultValue: '',
          });
          if (savedSignature) {
            setSignatureHtml(savedSignature);
          }
        } catch (error) {
          console.error('[CalendarInviteModal] Failed to load signature:', error);
        } finally {
          setLoadingSignature(false);
        }
      };
      loadSignature();
    }
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedEventId(null);
      setGuestName('');
      setGuestEmail('');
      setAxsEmail('');
      setTicketsConfirmed(2);
      setOptionConfirmed(1);
      setSearchQuery('');
      setShowEventDropdown(false);
      setShowSuccess(false);
    }
  }, [isOpen]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!showEventDropdown) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowEventDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEventDropdown]);

  // Auto-fill axsEmail from guestEmail if empty
  useEffect(() => {
    if (guestEmail && !axsEmail) {
      setAxsEmail(guestEmail);
    }
  }, [guestEmail, axsEmail]);

  // Get selected event
  const selectedEvent = useMemo(
    () => events.find((e) => e.id === selectedEventId) || null,
    [events, selectedEventId]
  );

  // Filter events based on search query
  const filteredEvents = useMemo(() => {
    if (!searchQuery.trim()) return events;
    const query = searchQuery.toLowerCase();
    return events.filter(
      (e) =>
        e.opponent?.toLowerCase().includes(query) ||
        e.venue?.toLowerCase().includes(query) ||
        e.event_date.includes(query)
    );
  }, [events, searchQuery]);

  // Check if form is valid
  const isFormValid = useMemo(() => {
    return (
      selectedEventId !== null &&
      guestName.trim() !== '' &&
      guestEmail.trim() !== '' &&
      axsEmail.trim() !== '' &&
      ticketsConfirmed > 0 &&
      // Basic email validation
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guestEmail.trim()) &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(axsEmail.trim())
    );
  }, [selectedEventId, guestName, guestEmail, axsEmail, ticketsConfirmed]);

  // Handle event selection
  const handleSelectEvent = useCallback((event: HMCEvent) => {
    setSelectedEventId(event.id);
    setShowEventDropdown(false);
    setSearchQuery('');
  }, []);

  // Handle opening Outlook Web App with pre-filled calendar invite
  const handleOpenInOutlook = useCallback(() => {
    if (!selectedEvent || !isFormValid) return;

    const config: CalendarInviteConfig = {
      event: selectedEvent,
      guestName: guestName.trim(),
      guestEmail: guestEmail.trim(),
      axsEmail: axsEmail.trim(),
      ticketsConfirmed,
      optionConfirmed,
      signatureHtml,
    };

    // Open Outlook Web App in new tab with pre-filled invite
    openOutlookCalendarInvite(config);

    // Show success state briefly
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
    }, 2000);
  }, [selectedEvent, isFormValid, guestName, guestEmail, axsEmail, ticketsConfirmed, optionConfirmed, signatureHtml]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-dark-surface border border-dark-border/30 rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-dark-border/30 bg-gradient-to-r from-dark-bg via-dark-surface to-dark-bg">
          <div className="flex items-center gap-2">
            <Calendar size={20} className="text-brand-400" />
            <h2 className="text-lg font-semibold text-white">
              Generate Calendar Invite
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-dark-muted hover:text-white hover:bg-dark-surface/50 rounded-lg transition-all duration-150 min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Event Selection */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-dark-text">
              <Calendar size={14} />
              Confirmed Event <span className="text-red-400">*</span>
            </label>
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setShowEventDropdown(!showEventDropdown)}
                className="w-full px-3 py-3 bg-dark-input border border-dark-border/30 rounded-xl text-left flex items-center justify-between hover:border-brand-500 transition-all duration-150"
              >
                {selectedEvent ? (
                  <div className="flex-1 min-w-0">
                    <span className="text-white font-medium">
                      {getCleanEventName(selectedEvent.opponent)}
                    </span>
                    {selectedEvent.event_date && (
                      <span className="text-dark-muted ml-2">
                        ({formatEventDate(selectedEvent.event_date)})
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-dark-muted">Select an event...</span>
                )}
                <ChevronDown
                  size={18}
                  className={`text-dark-muted transition-transform ${
                    showEventDropdown ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {/* Dropdown */}
              {showEventDropdown && (
                <div className="absolute z-50 w-full mt-1 bg-dark-surface border border-dark-border/30 rounded-xl shadow-2xl shadow-black/20 max-h-60 overflow-hidden">
                  {/* Search */}
                  <div className="p-2 border-b border-dark-border/30 sticky top-0 bg-dark-surface">
                    <div className="relative">
                      <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-muted"
                      />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search events..."
                        className="w-full pl-9 pr-3 py-2 bg-dark-input border border-dark-border/30 rounded-md text-white text-sm placeholder-dark-muted focus:outline-none focus:border-brand-500"
                        autoFocus
                      />
                    </div>
                  </div>

                  {/* Event list */}
                  <div className="overflow-y-auto max-h-48">
                    {filteredEvents.length === 0 ? (
                      <div className="p-3 text-sm text-dark-muted text-center">
                        No events found
                      </div>
                    ) : (
                      filteredEvents.map((event) => (
                        <button
                          key={event.id}
                          onClick={() => handleSelectEvent(event)}
                          className={`w-full px-3 py-2 text-left hover:bg-dark-surface/50 transition-all duration-150 ${
                            event.id === selectedEventId
                              ? 'bg-brand-500/20'
                              : ''
                          }`}
                        >
                          <div className="text-sm text-white font-medium">
                            {getCleanEventName(event.opponent)}
                          </div>
                          <div className="text-xs text-dark-muted flex items-center gap-2 mt-0.5">
                            {event.event_date && (
                              <span>{formatEventDate(event.event_date)}</span>
                            )}
                            {event.venue && (
                              <>
                                <span>•</span>
                                <span>{event.venue}</span>
                              </>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Guest Name */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-dark-text">
              <User size={14} />
              Guest Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              placeholder="John Smith"
              className="w-full px-3 py-2 bg-dark-input border border-dark-border/30 rounded-xl text-white placeholder-dark-muted focus:outline-none focus:border-brand-500"
            />
          </div>

          {/* Guest Email (for calendar invite) */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-dark-text">
              <Mail size={14} />
              Guest Email <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              placeholder="john@company.com"
              className="w-full px-3 py-2 bg-dark-input border border-dark-border/30 rounded-xl text-white placeholder-dark-muted focus:outline-none focus:border-brand-500"
            />
            <p className="text-xs text-dark-muted">
              Calendar invite will be addressed to this email
            </p>
          </div>

          {/* AXS Email (for ticket transfer) */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-dark-text">
              <Mail size={14} />
              AXS Ticket Transfer Email <span className="text-red-400">*</span>
            </label>
            <input
              type="email"
              value={axsEmail}
              onChange={(e) => setAxsEmail(e.target.value)}
              placeholder="john@company.com"
              className="w-full px-3 py-2 bg-dark-input border border-dark-border/30 rounded-xl text-white placeholder-dark-muted focus:outline-none focus:border-brand-500"
            />
            <p className="text-xs text-dark-muted">
              Email they provided for receiving tickets via AXS
            </p>
          </div>

          {/* Tickets Confirmed */}
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-dark-text">
              <Ticket size={14} />
              Tickets Confirmed <span className="text-red-400">*</span>
            </label>
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((count) => (
                <button
                  key={count}
                  type="button"
                  onClick={() => setTicketsConfirmed(count)}
                  className={`flex-1 py-2 rounded-lg font-medium transition-all duration-150 ${
                    ticketsConfirmed === count
                      ? 'bg-brand-500 text-white'
                      : 'bg-dark-input border border-dark-border/30 text-dark-text hover:border-brand-500'
                  }`}
                >
                  {count}
                </button>
              ))}
            </div>
          </div>

          {/* Option Confirmed */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-dark-text">
              Which Choice Was Confirmed? <span className="text-red-400">*</span>
            </label>
            <div className="flex gap-2">
              {[
                { value: 1, label: 'Primary' },
                { value: 2, label: '2nd Choice' },
                { value: 3, label: '3rd Choice' },
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setOptionConfirmed(option.value as OptionConfirmed)}
                  className={`flex-1 py-2 rounded-lg font-medium transition-all duration-150 text-sm ${
                    optionConfirmed === option.value
                      ? 'bg-green-500 text-white'
                      : 'bg-dark-input border border-dark-border/30 text-dark-text hover:border-green-500'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Email Signature Status */}
          <div className="p-3 bg-dark-header/50 rounded-lg">
            <div className="flex items-center gap-2">
              {loadingSignature ? (
                <>
                  <Loader2 size={14} className="text-dark-muted animate-spin" />
                  <span className="text-sm text-dark-muted">Loading signature...</span>
                </>
              ) : signatureHtml ? (
                <>
                  <CheckCircle size={14} className="text-green-400" />
                  <span className="text-sm text-green-400">
                    Your saved email signature will be included
                  </span>
                </>
              ) : (
                <span className="text-sm text-dark-muted">
                  No saved email signature found. Set one in the Email Template Generator.
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-dark-border/30 bg-dark-header/50">
          {showSuccess ? (
            <div className="flex items-center justify-center gap-2 py-2 text-green-400">
              <CheckCircle size={20} />
              <span className="font-medium">Outlook opened in new tab!</span>
            </div>
          ) : (
            <button
              onClick={handleOpenInOutlook}
              disabled={!isFormValid}
              className={`w-full py-3 rounded-lg font-semibold transition-all duration-150 flex items-center justify-center gap-2 ${
                isFormValid
                  ? 'bg-brand-500 hover:bg-brand-600 text-white'
                  : 'bg-dark-border text-dark-muted cursor-not-allowed'
              }`}
            >
              <ExternalLink size={18} />
              Open in Outlook
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarInviteModal;
